import React, { useEffect, useState } from "react";

// Shared "share this report via WhatsApp" modal — collects a mobile number and
// hands it back via onConfirm. Used by any AI report page (My AI Reports,
// Cosmetic Skin Care Results, ...) that offers a WhatsApp share action.
function WhatsAppShareModal({ isOpen, onClose, onConfirm, loading }) {
  const [mobileNumber, setMobileNumber] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) { setMobileNumber(""); setError(""); }
  }, [isOpen]);

  const handleShare = () => {
    const phoneRegex = /^[6-9]\d{9}$/;
    if (!mobileNumber.trim()) { setError("Please enter a mobile number"); return; }
    if (!phoneRegex.test(mobileNumber.trim())) {
      setError("Please enter a valid 10-digit mobile number starting with 6-9");
      return;
    }
    setError("");
    onConfirm(mobileNumber.trim());
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 9999, padding: "16px",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: "#fff", borderRadius: "16px", padding: "32px",
          maxWidth: "440px", width: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* WhatsApp icon */}
            <div style={{
              width: 36, height: 36, borderRadius: "50%",
              background: "#25D366", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <span style={{ fontSize: "18px", fontWeight: 700, color: "#1a1a1a", fontFamily: "Inter, sans-serif" }}>
              Share via WhatsApp
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: "4px", color: "#888", fontSize: "20px" }}
          >
            ✕
          </button>
        </div>

        <p style={{ fontSize: "14px", color: "#6b7280", marginBottom: "20px", fontFamily: "Inter, sans-serif" }}>
          Enter the mobile number where you want to send the report PDF.
        </p>

        {/* Input */}
        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "8px", fontFamily: "Inter, sans-serif" }}>
            Mobile Number (10 digits)
          </label>
          <input
            type="tel"
            value={mobileNumber}
            onChange={(e) => { setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10)); setError(""); }}
            placeholder="Enter 10-digit number"
            maxLength="10"
            style={{
              width: "100%", padding: "10px 14px", border: `1px solid ${error ? "#ef4444" : "#d1d5db"}`,
              borderRadius: "8px", fontSize: "15px", outline: "none", fontFamily: "Inter, sans-serif",
              boxSizing: "border-box", transition: "border-color 0.2s",
            }}
            onFocus={(e) => { e.target.style.borderColor = "#004276"; e.target.style.boxShadow = "0 0 0 3px rgba(0,66,118,0.1)"; }}
            onBlur={(e) => { e.target.style.borderColor = error ? "#ef4444" : "#d1d5db"; e.target.style.boxShadow = "none"; }}
          />
          {error && <p style={{ color: "#ef4444", fontSize: "12px", marginTop: "6px", fontFamily: "Inter, sans-serif" }}>{error}</p>}
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              flex: 1, padding: "10px 16px", border: "1px solid #d1d5db", borderRadius: "8px",
              background: "#fff", color: "#374151", fontWeight: 500, fontSize: "14px",
              cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.5 : 1,
              fontFamily: "Inter, sans-serif",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleShare}
            disabled={loading}
            style={{
              flex: 1, padding: "10px 16px", border: "none", borderRadius: "8px",
              background: loading ? "#86efac" : "#25D366", color: "#fff", fontWeight: 600, fontSize: "14px",
              cursor: loading ? "not-allowed" : "pointer", display: "flex", alignItems: "center",
              justifyContent: "center", gap: "8px", fontFamily: "Inter, sans-serif", transition: "background 0.2s",
            }}
          >
            {loading ? (
              <>
                <div style={{
                  width: "16px", height: "16px", border: "2px solid #fff",
                  borderTop: "2px solid transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite",
                }} />
                Sending...
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Send
              </>
            )}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default WhatsAppShareModal;
